/**
 * Render LLM-produced markdown safely.
 *
 * The text comes from an AI agent, so it is untrusted: it could contain
 * `<script>`, `<img onerror=…>` or `javascript:` links. We therefore (1) parse with
 * remarkable with raw-HTML parsing OFF — `html: false` is security-critical, never
 * set it true — so literal HTML in the source is escaped rather than parsed, and
 * (2) run the resulting HTML string through DOMPurify before injecting it, which
 * strips any dangerous tags/attributes/URLs as defence-in-depth.
 *
 * Both `remarkable` and `dompurify` are direct dependencies (see package.json).
 */
import { useMemo } from 'react';
import Box from '@mui/joy/Box';
import { Remarkable } from 'remarkable';
import { linkify } from 'remarkable/linkify';
import DOMPurify from 'dompurify';

const md = new Remarkable('full', {
  html: false,      // security-critical: do NOT parse raw HTML in the source
  breaks: true,     // single newlines become <br> — reads better in a chat bubble
  typographer: false,
}).use(linkify);    // auto-link bare URLs (remarkable v2 moved this to a plugin)

// Force every link to open in a new tab without leaking window.opener. DOMPurify
// has already removed `javascript:` URLs and event handlers, so the href is safe.
// This is our own DOMPurify instance (direct dep), separate from the one
// swagger-ui-react bundles, so the hook doesn't affect link behaviour elsewhere.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

export default function Markdown({ text, sx }) {
  const html = useMemo(() => DOMPurify.sanitize(md.render(String(text ?? ''))), [text]);

  return (
    <Box
      dangerouslySetInnerHTML={{ __html: html }}
      sx={{
        color: 'inherit',
        fontSize: 'sm',
        lineHeight: 'md',
        overflowWrap: 'anywhere',
        '& > :first-of-type': { mt: 0 },
        '& > :last-child': { mb: 0 },
        '& p': { my: 0.5 },
        '& h1, & h2, & h3, & h4, & h5, & h6': { fontSize: 'md', fontWeight: 'lg', my: 0.75 },
        '& ul, & ol': { my: 0.5, pl: 2.5 },
        '& li': { my: 0.25 },
        '& a': { color: 'primary.500', textDecoration: 'underline' },
        '& code': { fontFamily: 'code', fontSize: '0.85em', bgcolor: 'background.level1', px: 0.5, py: '1px', borderRadius: 'xs' },
        '& pre': { my: 0.5, p: 1, bgcolor: 'background.level1', borderRadius: 'sm', overflow: 'auto' },
        '& pre code': { bgcolor: 'transparent', p: 0, fontSize: '0.85em' },
        '& blockquote': { my: 0.5, ml: 0, pl: 1.5, borderLeft: '3px solid', borderColor: 'neutral.outlinedBorder', color: 'text.secondary' },
        '& table': { borderCollapse: 'collapse', my: 0.5 },
        '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1, py: 0.5 },
        '& img': { maxWidth: '100%' },
        '& hr': { my: 1, border: 0, borderTop: '1px solid', borderColor: 'divider' },
        ...sx,
      }}
    />
  );
}
